import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import EmailComposeModal from './EmailComposeModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { 
  getAllUsers, 
  getUserAuthorizations, 
  addUserAuthorization, 
  updateUserAuthorization,
  deleteUserAuthorization,
  isEmailAuthorized,
  sendAuthorizationEmail
} from '../../../services/userService';

const UserManagementTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [users, setUsers] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'city_official', label: 'City Officials' },
    { value: 'student', label: 'Students' },
    { value: 'pcf_admin', label: 'PCF Admins' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'authorized', label: 'Authorized' },
    { value: 'inactive', label: 'Inactive' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [usersData, authorizationsData] = await Promise.all([
        getAllUsers(),
        getUserAuthorizations()
      ]);

      setUsers(usersData || []);
      setAuthorizations(authorizationsData || []);
      
      // Update total users count from role_email_authorizations
      setTotalUsers(authorizationsData?.length || 0);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = () => {
    fetchData();
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'city_official': 'City Official',
      'student': 'Student',
      'pcf_admin': 'PCF Admin'
    };
    return roleMap?.[role] || role;
  };

  const getStatusBadge = (user, authorization) => {
    let status = 'inactive';
    let label = 'Inactive';

    if (user) {
      status = 'active';
      label = 'Active';
    } else if (authorization) {
      status = 'authorized';
      label = 'Authorized';
    }

    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      authorized: { color: 'bg-warning text-warning-foreground', label: 'Authorized' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Combine users and authorizations
  const combinedData = [];
  const processedEmails = new Set();

  // Add active users first
  users?.forEach(user => {
    // Find matching authorization to get team_role if exists
    const matchingAuth = authorizations?.find(
      auth => auth?.email?.toLowerCase() === user?.email?.toLowerCase()
    );

    combinedData?.push({
      id: user?.id,
      name: user?.full_name,
      email: user?.email,
      role: user?.role,
      status: 'active',
      organization: user?.organization || 'N/A',
      teamNumber: matchingAuth?.team_number || null,
      teamRole: matchingAuth?.team_role || null,
      lastActive: user?.updated_at || user?.created_at,
      joinDate: user?.created_at,
      isUser: true
    });
    processedEmails?.add(user?.email?.toLowerCase());
  });

  // Add authorized users who haven't signed up yet
  authorizations?.forEach(auth => {
    if (!processedEmails?.has(auth?.email?.toLowerCase())) {
      combinedData?.push({
        id: auth?.id,
        name: 'Not Registered',
        email: auth?.email,
        role: auth?.role,
        status: 'authorized',
        organization: 'N/A',
        teamNumber: auth?.team_number,
        teamRole: auth?.team_role,
        lastActive: null,
        joinDate: auth?.created_at,
        isUser: false
      });
    }
  });

  const filteredUsers = combinedData?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.status === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Helper function to display team role based on user role
  const getTeamRoleDisplay = (user) => {
    // Show N/A for pcf_admin and city_official
    if (user?.role === 'pcf_admin' || user?.role === 'city_official') {
      return 'N/A';
    }
    
    // For students, show team_role if available, otherwise N/A
    if (user?.role === 'student') {
      return user?.teamRole || 'N/A';
    }
    
    return 'N/A';
  };

  const handleAddUser = async (userData) => {
    try {
      // Check if email is already authorized
      const emailExists = await isEmailAuthorized(userData?.email);
      if (emailExists) {
        throw new Error('This email is already authorized');
      }

      // Add user authorization
      const result = await addUserAuthorization(userData);
      
      if (result?.error) {
        throw result?.error;
      }

      // Send authorization email
      const emailResult = await sendAuthorizationEmail({
        email: userData?.email,
        fullName: userData?.fullName,
        role: userData?.role
      });

      if (!emailResult?.success) {
        console.error('Failed to send authorization email:', emailResult?.error);
        
        // Check if it's a configuration error
        const isConfigError = emailResult?.error?.includes('RESEND_API_KEY') || 
                             emailResult?.error?.includes('Email service not configured');
        
        if (isConfigError) {
          alert(
            `✅ User added successfully!\n\n` +
            `⚠️ Authorization email could not be sent:\n${emailResult?.error}\n\n` +
            `📋 Setup Instructions:\n` +
            `1. Go to your Supabase Dashboard\n` +
            `2. Navigate to Edge Functions → send-email\n` +
            `3. Add RESEND_API_KEY to function secrets\n` +
            `4. Get your API key from resend.com\n\n` +
            `The user can still register manually at the signup page.`
          );
        } else {
          alert(
            `✅ User added successfully!\n\n` +
            `⚠️ Authorization email failed: ${emailResult?.error}\n\n` +
            `Please verify your email service configuration.`
          );
        }
      }

      // Refresh data
      await fetchData();

      // Show success message (you could add a toast notification here)
      console.log('User added successfully and authorization email sent');
    } catch (err) {
      console.error('Error adding user:', err);
      throw err;
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await updateUserAuthorization(userId, {
        email: updates?.email?.toLowerCase(),
        role: updates?.role,
        team_number: updates?.teamNumber || null,
        team_role: updates?.teamRole || null
      });

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const handleEmailUser = (user) => {
    setSelectedUser(user);
    setIsEmailModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    setIsDeleting(true);
    try {
      await deleteUserAuthorization(selectedUser?.id, selectedUser?.email);
      
      // Refresh data
      await fetchData();
      
      // Close modal
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-muted-foreground mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="AlertCircle" size={20} className="text-destructive" />
          <h3 className="font-medium text-destructive">Error Loading Users</h3>
        </div>
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button onClick={fetchData} variant="outline" className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage all platform users
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={loadUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setIsAddUserModalOpen(true)}
          >
            Add User
          </Button>
        </div>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">Total Users</p>
              <p className="text-xl sm:text-2xl font-semibold text-foreground mt-1">{totalUsers}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Users" size={20} className="sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">Active Users</p>
              <p className="text-xl sm:text-2xl font-semibold text-foreground mt-1">
                {filteredUsers?.filter(user => user?.status === 'active')?.length}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-success/10 flex items-center justify-center">
              <Icon name="Check" size={20} className="sm:w-6 sm:h-6 text-success" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">Authorized Users</p>
              <p className="text-xl sm:text-2xl font-semibold text-foreground mt-1">
                {filteredUsers?.filter(user => user?.status === 'authorized')?.length}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon name="Shield" size={20} className="sm:w-6 sm:h-6 text-warning" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">Inactive Users</p>
              <p className="text-xl sm:text-2xl font-semibold text-foreground mt-1">
                {filteredUsers?.filter(user => user?.status === 'inactive')?.length}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-muted/10 flex items-center justify-center">
              <Icon name="Clock" size={20} className="sm:w-6 sm:h-6 text-muted-foreground" />
            </div>
          </div>
        </div>
      </div>
      {/* Filters and Search */}
      <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="sm:col-span-2">
            <Input
              type="search"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select
              options={roleOptions}
              value={filterRole}
              onChange={setFilterRole}
              placeholder="Filter by role"
              className="w-full"
            />
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Filter by status"
              className="w-full"
            />
          </div>
        </div>
      </div>
      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Team Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Join Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-foreground">{getRoleDisplay(user?.role)}</span>
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(user?.isUser ? user : null, !user?.isUser ? user : null)}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground">{user?.organization}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground">{getTeamRoleDisplay(user)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground">{formatDate(user?.joinDate)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Edit user"
                      >
                        <Icon name="Edit" size={16} className="text-muted-foreground hover:text-foreground" />
                      </button>
                      <button
                        onClick={() => handleEmailUser(user)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Send email"
                      >
                        <Icon name="Mail" size={16} className="text-muted-foreground hover:text-foreground" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        title="Delete user"
                      >
                        <Icon name="Trash2" size={16} className="text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredUsers?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
            <p className="text-sm text-muted-foreground">
              {searchTerm || filterRole !== 'all' || filterStatus !== 'all' ?'Try adjusting your filters' :'Get started by adding your first user'}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onUserAdded={handleAddUser}
      />

      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUserUpdated={handleUpdateUser}
      />

      <EmailComposeModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setSelectedUser(null);
        }}
        recipientEmail={selectedUser?.email}
        recipientName={selectedUser?.name}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmDelete}
        userName={selectedUser?.name || selectedUser?.email}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default UserManagementTab;